// src/utils/documentoUtils.js

// Definição das regras de documentos
export const documentoRegras = {
  CPF: {
    tamanho: 11,
    placeholder: "000.000.000-00",
    mascara: "###.###.###-##",
    label: "CPF",
    regex: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
    mensagemErro: "CPF inválido (11 dígitos necessários)"
  },
  CNPJ: {
    tamanho: 14,
    placeholder: "00.000.000/0000-00",
    mascara: "##.###.###/####-##",
    label: "CNPJ",
    regex: /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/,
    mensagemErro: "CNPJ inválido (14 dígitos necessários)"
  },
  CNH: {
    tamanho: 11,
    placeholder: "00000000000",
    mascara: "###########",
    label: "CNH",
    regex: /^\d{11}$/,
    mensagemErro: "CNH inválida (11 dígitos necessários)"
  },
  RNE: {
    tamanho: 9,
    placeholder: "000000000",
    mascara: "#########",
    label: "RNE",
    regex: /^\d{9}$/,
    mensagemErro: "RNE inválido (9 dígitos necessários)"
  }
};

// Lista de opções para uso em selects
export const documentoOpcoes = Object.keys(documentoRegras).map(key => ({
  valor: key,
  texto: documentoRegras[key].label
}));

// Função para validar documento com base no tipo
export const validarDocumento = (documento, tipo) => {
  // Se tipo for indefinido ou não existir, usa CPF como padrão
  const tipoValido = tipo && documentoRegras[tipo] ? tipo : 'CPF';
  
  if (!documento || documento.trim() === '') {
    return { valido: false, mensagem: 'Campo obrigatório' };
  }

  const regra = documentoRegras[tipoValido];
  
  // Verifica se o formato com máscara está correto
  const valido = regra.regex.test(documento);


    return {
      valido,
      mensagem: valido ? '' : regra.mensagemErro
    };
  
};

// Função para remover máscara do documento
export const removerMascaraDocumento = (documento) => {
  return documento ? documento.replace(/[^\d]/g, '') : '';
};

// Função para obter a máscara com base no tipo de documento
export const obterMascaraDocumento = (tipo) => {
  // Se tipo for indefinido ou não existir, usa CPF como padrão
  const tipoValido = tipo && documentoRegras[tipo] ? tipo : 'CPF';
  return documentoRegras[tipoValido].mascara;
};

// Função para obter o placeholder com base no tipo de documento
export const obterPlaceholderDocumento = (tipo) => {
  // Se tipo for indefinido ou não existir, usa CPF como padrão
  const tipoValido = tipo && documentoRegras[tipo] ? tipo : 'CPF';
  return documentoRegras[tipoValido].placeholder;
};

// Função para obter o label com base no tipo de documento
export const obterLabelDocumento = (tipo) => {
  // Se tipo for indefinido ou não existir, usa CPF como padrão
  const tipoValido = tipo && documentoRegras[tipo] ? tipo : 'CPF';
  return documentoRegras[tipoValido].label;
};

// Função para validar CPF (algoritmo completo)
export const validarCPF = (cpf) => {
  const cpfLimpo = removerMascaraDocumento(cpf);
  
  if (!cpfLimpo || cpfLimpo.length !== 11) {
    return false;
  }

  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cpfLimpo)) {
    return false;
  }

  let soma = 0;
  let resto;

  // Validação do primeiro dígito verificador
  for (let i = 1; i <= 9; i++) {
    soma = soma + parseInt(cpfLimpo.substring(i - 1, i)) * (11 - i);
  }
  resto = (soma * 10) % 11;
  
  if ((resto === 10) || (resto === 11)) {
    resto = 0;
  }
  if (resto !== parseInt(cpfLimpo.substring(9, 10))) {
    return false;
  }

  // Validação do segundo dígito verificador
  soma = 0;
  for (let i = 1; i <= 10; i++) {
    soma = soma + parseInt(cpfLimpo.substring(i - 1, i)) * (12 - i);
  }
  resto = (soma * 10) % 11;
  
  if ((resto === 10) || (resto === 11)) {
    resto = 0;
  }
  if (resto !== parseInt(cpfLimpo.substring(10, 11))) {
    return false;
  }

  return true;
};

// Função para validar CNPJ (algoritmo completo)
export const validarCNPJ = (cnpj) => {
  const cnpjLimpo = removerMascaraDocumento(cnpj);
  
  if (!cnpjLimpo || cnpjLimpo.length !== 14) {
    return false;
  }

  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cnpjLimpo)) {
    return false;
  }

  // Validação do primeiro dígito verificador
  let tamanho = cnpjLimpo.length - 2;
  let numeros = cnpjLimpo.substring(0, tamanho);
  const digitos = cnpjLimpo.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  if (resultado !== parseInt(digitos.charAt(0))) {
    return false;
  }

  // Validação do segundo dígito verificador
  tamanho = tamanho + 1;
  numeros = cnpjLimpo.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;
  
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  if (resultado !== parseInt(digitos.charAt(1))) {
    return false;
  }

  return true;
};

// Função de validação completa que considera o algoritmo para CPF e CNPJ
export const validarDocumentoCompleto = (documento, tipo) => {
  // Primeiro verifica o formato
  const formatoValidacao = validarDocumento(documento, tipo);
  if (!formatoValidacao.valido) {
    return formatoValidacao;
  }

  // Se for CPF, valida o algoritmo
  if (tipo === 'CPF') {
    console.log('entrou aqui');
    const cpfValido = validarCPF(documento);
    return {
      valido: cpfValido,
      mensagem: cpfValido ? '' : 'CPF inválido (dígitos verificadores incorretos)'
    };
  }

  // Se for CNPJ, valida o algoritmo
  if (tipo === 'CNPJ') {
    const cnpjValido = validarCNPJ(documento);
    return {
      valido: cnpjValido,
      mensagem: cnpjValido ? '' : 'CNPJ inválido (dígitos verificadores incorretos)'
    };
  }

  // Para outros tipos, a validação de formato é suficiente
  return formatoValidacao;
};

export default {
  documentoRegras,
  documentoOpcoes,
  validarDocumento,
  removerMascaraDocumento,
  obterMascaraDocumento,
  obterPlaceholderDocumento,
  obterLabelDocumento,
  validarCPF,
  validarCNPJ,
  validarDocumentoCompleto
};